import React from "react";
import { useParams, Navigate, NavLink } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import WhyCloudare from "../components/WhyCloudare";
import FaqSection from "../components/FaqSection";
import ContactCTA from "../components/ContactCTA";
import useReveal from "../hooks/useReveal";
import { ERP_PAGES } from "../data/erpPages";
import "../styles/tax-page.css";
import "../styles/erp-page.css";

/**
 * ErpPage — single template for every /erp/{slug} platform page.
 * Pure document flow (reuses WhyCloudare grid), so no overlap bugs.
 */
export default function ErpPage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.platform;
  const data = ERP_PAGES[slug];
  useReveal([slug]);

  if (!data) return <Navigate to="/services/oracle" replace />;

  return (
    <React.Fragment>
      <Seo
        path={`/erp/${slug}`}
        title={`${data.hero.h1} | Cloudare`}
        description={data.hero.subhead}
        serviceName={data.hero.h1}
        breadcrumb={[
          { name: "Home", path: "/" },
          { name: "E-Invoicing + ERP", path: "/einvoicing-erp-integration" },
          { name: data.hero.h1, path: `/erp/${slug}` },
        ]}
      />
      <Navbar />

      <section className="tax-hero reveal">
        <div className="tax-hero__inner">
          <p className="tax-hero__eyebrow">{data.hero.eyebrow}</p>
          <h1 className="tax-hero__title">{data.hero.h1}</h1>
          <p className="tax-hero__subhead">{data.hero.subhead}</p>
          <div className="tax-hero__cta">
            <a className="btn-primary" href="https://calendly.com/cloudare" target="_blank" rel="noopener noreferrer">
              Book a discovery call
            </a>
            <NavLink className="btn-secondary" to="/contact-us">
              Contact us
            </NavLink>
          </div>
        </div>
      </section>

      <section className="tax-snapshot">
        <div className="tax-snapshot__grid reveal-stagger">
          {data.snapshot.map((s) => (
            <div className="snapshot-card" key={s.label}>
              <span className="snapshot-card__label">{s.label}</span>
              <span className="snapshot-card__value">{s.value}</span>
            </div>
          ))}
        </div>
      </section>

      <WhyCloudare title={data.whyTitle} lede={data.whyLede} features={data.features} elevated />

      {/* Tech stack chips */}
      <section className="erp-stack">
        <header className="why-cloudare__header reveal">
          <p className="why-cloudare__eyebrow">Technology</p>
          <h2 className="why-cloudare__title">Stack we work in</h2>
        </header>
        <div className="erp-chips reveal">
          {data.stack.map((s) => (
            <span className="erp-chip" key={s}>{s}</span>
          ))}
        </div>
      </section>

      <div id="faq">
        <FaqSection
          eyebrow="Frequently asked questions"
          title={`${data.countryCode} — FAQ`}
          faqs={data.faqs}
          emitJsonLd
        />
      </div>

      <ContactCTA
        title={`Talk to our ${data.countryCode} team`}
        subtitle="Share your modules, versions and compliance needs — we'll scope the right approach."
      />
      <Footer />
    </React.Fragment>
  );
}
